import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyGetNodeDataByTimestampsQuery } from 'services/devices/devices.api';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Points for each device in one page. The backend caps `limit` at 5000. */
export const DEFAULT_SERIES_PAGE_SIZE = 100;

/** Device ids in one request. The backend answers 400 above this. */
export const MAX_IDS_PER_REQUEST = 25;

export const chunkIds = (ids, size = MAX_IDS_PER_REQUEST) => {
  const chunks = [];
  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size));
  }
  return chunks;
};

/** A missing bound falls back to the last 24 hours, the page-load window. */
export const resolveSeriesRange = (startTime, endTime, now = Date.now()) => {
  const end = Number.isFinite(endTime) ? endTime : now;
  const start = Number.isFinite(startTime) ? startTime : end - DAY_MS;
  return { startTime: start, endTime: end };
};

const normalizeIds = (ids) =>
  (Array.isArray(ids) ? ids : []).filter((id) => id !== null && id !== undefined && id !== '');

/** Shown to a person when a page fails, so keep it readable. */
const FALLBACK_ERROR_MESSAGE = 'Device data could not be loaded.';

/**
 * The message for a failed page.
 *
 * A rejected request answers with the shared `validate()` envelope, whose
 * `message` is always the constant 'Invalid request' — the reason a person can
 * act on is in `errors[0].message`, so read that first. `rtkBaseQuery` hands the
 * body back as `raw`; a plain axios rejection carries it on `response.data`.
 */
export const resolveSeriesErrorMessage = (error) => {
  const body = error?.raw ?? error?.data ?? error?.response?.data ?? null;
  return body?.errors?.[0]?.message ?? body?.message ?? error?.message ?? FALLBACK_ERROR_MESSAGE;
};

/**
 * Fetches a paged series of data points for named devices, on demand.
 *
 * The page load holds ONE row for each device (`data[0]`). A screen that shows a
 * series — a datasheet, a history table — calls this hook with the device ids it
 * shows and the window it shows them in. Page 0 (the newest `limit` points for
 * each device) is fetched when the ids, the window or `enabled` change. `loadMore`
 * walks backwards in time one page at a time while `hasMore` is true.
 *
 * The points land in the `getConnectedDevices` cache through the lazy query's
 * `onQueryStarted` merge, so callers keep reading `device.data`. Only the paging
 * state is returned here.
 *
 * @param {{
 *   ids: Array<number | string>,
 *   startTime?: number,
 *   endTime?: number,
 *   limit?: number,
 *   enabled?: boolean,
 * }} options
 * A failed page is NOT a drained series: `error` carries the reason so a caller
 * can say so instead of showing its empty state, and `loadMore` stays callable
 * as a retry of the page that failed.
 *
 * @returns {{
 *   loadMore: () => void,
 *   hasMore: boolean,
 *   isFetching: boolean,
 *   offset: number,
 *   error: { message: string } | null,
 * }}
 */
export default function useDeviceSeriesData({
  ids,
  startTime,
  endTime,
  limit = DEFAULT_SERIES_PAGE_SIZE,
  enabled = true,
}) {
  const [fetchRange] = useLazyGetNodeDataByTimestampsQuery();

  // Key the fetch on the VALUES, not the array identity: callers rebuild `ids`
  // on every render and the effect must not refetch for that.
  const idList = normalizeIds(ids);
  const idsKey = idList.map(String).join(',');
  const requestKey =
    enabled && idsKey ? `${idsKey}|${startTime ?? ''}|${endTime ?? ''}|${limit}` : null;

  // The paging state belongs to ONE request key. A different key reads as
  // "nothing fetched yet", so a closed or switched datasheet never shows the
  // previous device's Load more.
  const [paging, setPaging] = useState({ key: null, offset: 0, hasMore: false, error: null });
  const [isFetching, setIsFetching] = useState(() => Boolean(requestKey));
  const idsRef = useRef(idList);
  idsRef.current = idList;
  const rangeRef = useRef(null); // the window page 0 used; later pages walk the same one
  const requestIdRef = useRef(0); // a newer request supersedes an older one still in flight

  const fetchPage = useCallback(
    async (key, offset, range) => {
      const requestId = ++requestIdRef.current;
      setIsFetching(true);
      try {
        const responses = await Promise.all(
          chunkIds(idsRef.current).map((chunk) =>
            fetchRange({
              startTime: range.startTime,
              endTime: range.endTime,
              ids: chunk,
              limit,
              offset,
            }).unwrap(),
          ),
        );
        if (requestId !== requestIdRef.current) return;
        const hasMore = responses.some((response) => Boolean(response?.page?.hasMore));
        // A page that lands clears the previous failure: the series is healthy again.
        setPaging({ key, offset: offset + limit, hasMore, error: null });
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error('Device series fetch failed:', error);
        // Keep `offset` where it was so `loadMore` retries the page that failed,
        // and hold the reason so the caller shows an error, not an empty series.
        setPaging({
          key,
          offset,
          hasMore: false,
          error: { message: resolveSeriesErrorMessage(error) },
        });
      } finally {
        if (requestId === requestIdRef.current) setIsFetching(false);
      }
    },
    [fetchRange, limit],
  );

  useEffect(() => {
    if (!requestKey) {
      requestIdRef.current += 1; // drop the result of anything still in flight
      setIsFetching(false);
      return;
    }
    const range = resolveSeriesRange(startTime, endTime);
    rangeRef.current = range;
    fetchPage(requestKey, 0, range);
  }, [requestKey, startTime, endTime, fetchPage]);

  // A different request key reads as "nothing fetched yet", which is also what
  // clears a previous key's error.
  const current = paging.key === requestKey ? paging : null;
  const hasMore = Boolean(current?.hasMore);
  const error = current?.error ?? null;

  const loadMore = useCallback(() => {
    if (!requestKey || isFetching) return;
    // After a failure there is no `hasMore` to go on, but the page is still
    // owed: let the same control retry it.
    if (!current?.hasMore && !current?.error) return;
    fetchPage(
      requestKey,
      current.offset,
      rangeRef.current ?? resolveSeriesRange(startTime, endTime),
    );
  }, [requestKey, current, isFetching, fetchPage, startTime, endTime]);

  return { loadMore, hasMore, isFetching, offset: current?.offset ?? 0, error };
}
